// 13 · Mi cuenta — datos, suscripción, preferencias de alertas y zona de
// peligro. Sin precio ni CTA de compra (decisión 5: el cobro vive en la web).
// Datos reales: Firestore users/{uid} (nombre, plan, telegram), Auth
// (emailVerified). Cambiar contraseña = resetPassword (Resend).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../firebase';
import { useAuth } from '../../../auth/AuthContext';
import { clearSessionExpiredFlag, resetPassword } from '../../../auth/authHelpers';
import { Nav, I, useToast } from '../ui';

const MENU_ICON = { card: <I.card />, doc: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4M5 21V3h9l5 5v13z" /></svg> };

export default function MiCuenta() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, userData, isAdmin, hasActiveAccess, accessUntil, signOut } = useAuth();
  const [editando, setEditando] = useState(false);
  const [nombreIn, setNombreIn] = useState('');
  const [confirmDel, setConfirmDel] = useState(0); // 0 | 1 | 2

  const nombre = userData?.nombre || user?.displayName || (user?.email || '').split('@')[0] || 'Cliente';
  const inicial = nombre.trim().charAt(0).toUpperCase();
  const verificado = !!user?.emailVerified;
  const esSub = !!userData?.flowSubscriptionId || !!userData?.paypal_subscription_id;
  const medio = userData?.paypal_subscription_id ? 'PayPal' : userData?.flowSubscriptionId || userData?.flowCustomerId ? 'Webpay' : null;
  const vence = accessUntil
    ? new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', day: 'numeric', month: 'long' })
        .format(accessUntil.seconds ? new Date(accessUntil.seconds * 1000) : new Date(accessUntil))
    : null;

  const guardarNombre = async () => {
    const n = nombreIn.trim();
    if (n.length < 2) { toast('Escribe un nombre de al menos 2 letras.'); return; }
    try { await updateDoc(doc(firestore, 'users', user.uid), { nombre: n }); toast('Nombre actualizado', { ok: true, ms: 2500 }); setEditando(false); }
    catch (_e) { toast('No pudimos guardar el nombre', { action: 'Reintentar', onAction: guardarNombre }); }
  };
  const cambiarPass = async () => {
    try { await resetPassword(user.email); toast('Te enviamos un correo para crear una nueva contraseña', { ok: true }); }
    catch (_e) { toast('No pudimos enviar el correo', { action: 'Reintentar', onAction: cambiarPass }); }
  };
  const cerrar = async () => { clearSessionExpiredFlag(); await signOut(); navigate('/app', { replace: true }); };
  const eliminar = () => {
    // No existe callable de borrado: la solicitud se procesa por soporte
    // (borrado de datos en 30 días). Confirmación en dos pasos.
    if (confirmDel < 2) { setConfirmDel(confirmDel + 1); return; }
    window.location.href = `mailto:contacto@edgeelite.cl?subject=${encodeURIComponent('Eliminar mi cuenta')}&body=${encodeURIComponent(`Solicito eliminar la cuenta ${user?.email} y borrar mis datos.`)}`;
    setConfirmDel(0);
  };

  const planTitulo = isAdmin ? 'Cuenta admin' : hasActiveAccess ? (esSub ? 'Plan mensual' : 'Acceso activo') : 'Sin plan activo';
  const planSub = isAdmin ? 'Acceso completo' : hasActiveAccess ? (vence ? `${esSub ? 'Renueva' : 'Vence'} el ${vence}` : 'Vigente') : 'Tu acceso no está activo';

  return (
    <div className="v3"><div className="scroll nt safe-top">
      <Nav title="Mi cuenta" onBack={() => navigate('/app/perfil')} />
      <div className="avbig">{inicial}<button type="button" className="ed" aria-label="Editar nombre" onClick={() => { setNombreIn(nombre); setEditando(true); }}><I.pen /></button></div>

      <p className="grp">Datos</p>
      <div className="fields">
        <div className={`fld${editando ? ' focus' : ''}`}><p className="l">Nombre</p><div className="row2">
          {editando ? (
            <>
              <input autoFocus value={nombreIn} onChange={(e) => setNombreIn(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && guardarNombre()} />
              <button type="button" onClick={guardarNombre} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>Guardar</button>
            </>
          ) : (<><span className="val">{nombre}</span><button type="button" onClick={() => { setNombreIn(nombre); setEditando(true); }} style={{ background: 'none', border: 'none', fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Cambiar</button></>)}
        </div></div>
        <div className="fld"><p className="l">Correo</p><div className="row2">
          <span className="val" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</span>
          {verificado ? <span className="badge-ok"><I.check width={11} height={11} strokeWidth="3.2" />Verificado</span> : <span className="badge-off">Sin verificar</span>}
        </div></div>
        <div className="fld"><p className="l">Contraseña</p><div className="row2">
          <span className="val">••••••••••</span>
          <button type="button" onClick={cambiarPass} style={{ background: 'none', border: 'none', fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Cambiar</button>
        </div></div>
      </div>

      <p className="grp">Suscripción</p>
      <div className="menu">
        <div className="mi"><span className="ico">{MENU_ICON.card}</span><span className="t">{planTitulo}<small>{planSub}</small></span>
          {isAdmin || hasActiveAccess ? <span className="badge-ok">Activo</span> : <span className="badge-off">Inactivo</span>}</div>
        {medio && <div className="mi"><span className="ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16v16H4zM4 9h16" /></svg></span><span className="t">Medio de pago</span><span className="val">{medio}</span></div>}
      </div>

      {/* Alertas (toggles): OCULTO por decisión Matías 2026-09-10 hasta que
          telegram_alerts.py lea users/{uid}.alertPrefs. Hook useAlertPrefs
          queda listo; reactivar cuando el bot aplique la preferencia. */}
      <p className="grp">Sesión</p>
      <div className="menu">
        <button type="button" className="mi out" onClick={cerrar}><span className="t">Cerrar sesión</span></button>
        <button type="button" className="mi out" style={{ color: confirmDel ? 'var(--neg)' : 'var(--muted)' }} onClick={eliminar}>
          <span className="t">{confirmDel === 0 ? 'Eliminar cuenta' : confirmDel === 1 ? '¿Seguro? Toca de nuevo para confirmar' : 'Confirmar eliminación'}<small>Borra tus datos en 30 días</small></span>
        </button>
      </div>
    </div></div>
  );
}
